import React, { useRef, useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    getArtifact,
    getArtifactVersions,
    submitForApproval,
    resendNotification,
} from '../../../../services/artifact-api';
import Toast from '../../../../design-system/components/Toast/Toast';

// A PRD can be sent to approval while it is a draft or after the manager asked
// for changes. Once it is pending or approved, the submit action is hidden.
const SUBMITTABLE_STATUSES = ['draft', 'changes_requested'];

// Presentation for a manager decision. `trusted` marks whether the comment is
// reliably attributed to THIS decision — true for the per-version audit trail,
// false for the artifact-level fallback. The artifact carries a single
// decisionComment that later rounds overwrite, so on a rejected fallback the
// stored comment may actually be an earlier round's change request; we hide it
// to avoid showing a misattributed reason (a rejected reason is optional
// anyway). changes_requested is unambiguous (it IS the active request), so its
// comment shows even in the fallback.
function decisionView(decision, comment, trusted) {
    const trimmed = (comment || '').trim();
    switch (decision) {
        case 'changes_requested':
            return {
                variant: 'changes',
                title: 'Cambios solicitados por tu supervisor',
                comment: trimmed,
            };
        case 'rejected':
            return {
                variant: 'rejected',
                title: 'Propuesta rechazada por tu supervisor',
                comment: trusted ? trimmed : '', // untrusted reject → label only
            };
        case 'approved':
            return {
                variant: 'approved',
                title: 'Aprobado por tu supervisor',
                comment: trusted ? trimmed : '',
            };
        default:
            return null;
    }
}

export default function ArtifactSidebar({ artifact, onClose, isBlocked, onSubmitted }) {
    const contentRef = useRef(null);
    const [versions, setVersions] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState(artifact.version || 1);
    const [displayContent, setDisplayContent] = useState(artifact.content);
    const [isLoadingVersion, setIsLoadingVersion] = useState(false);

    // Approval state. `status` falls back to 'draft' because the chat flow opens
    // the sidebar without a status field (only ArtifactList maps it today); the
    // effect below refreshes it (and the decision comment) from the API so the
    // banner works regardless of where the sidebar was opened from.
    const [status, setStatus] = useState(artifact.status || 'draft');
    const [decisionComment, setDecisionComment] = useState(artifact.decisionComment);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // deliveryFailed === true when the transition committed but the email did
    // not send, so we can offer a "resend" instead of a fresh submit.
    const [deliveryFailed, setDeliveryFailed] = useState(false);
    const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

    const showToast = (severity, message) => setToast({ open: true, severity, message });
    const closeToast = () => setToast((t) => ({ ...t, open: false }));

    // A blocked conversation (its PRD was rejected) is terminal: no submit.
    const canSubmit = !isBlocked && SUBMITTABLE_STATUSES.includes(status);

    // Decision to show. Prefer the per-version audit trail (precise, per
    // version) when present; otherwise fall back to the artifact's last decision
    // (status + decisionComment from GET /:id). The fallback covers PRDs decided
    // before the per-version history existed, or whose version row lacks the
    // decision. Independent of the version selector so it shows even with a
    // single version.
    const selectedVersionMeta = versions.find((v) => v.version === selectedVersion);
    const selectedDecision =
        (selectedVersionMeta && selectedVersionMeta.decision
            ? decisionView(selectedVersionMeta.decision, selectedVersionMeta.decisionComment, true)
            : null) || decisionView(status, decisionComment, false);

    const handleSubmitForApproval = async () => {
        setIsSubmitting(true);
        try {
            const res = await submitForApproval(artifact.id);
            setStatus(res.status || 'pending_approval');
            // Optimistically lock the conversation composer the instant the
            // submit succeeds (the backend 409 is only the backstop). Optional
            // because ArtifactList opens this sidebar without a conversation.
            if (onSubmitted) onSubmitted();
            if (res.notified) {
                setDeliveryFailed(false);
                showToast('success', `Enviado a revisión. Se notificó a ${res.managerEmail || 'tu manager'}.`);
            } else {
                setDeliveryFailed(true);
                showToast('warning', 'Se envió a revisión, pero el correo no pudo entregarse. Podés reintentar el envío.');
            }
        } catch (err) {
            showToast('error', err.message || 'No se pudo enviar a revisión.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResend = async () => {
        setIsSubmitting(true);
        try {
            const res = await resendNotification(artifact.id);
            if (res.notified) {
                setDeliveryFailed(false);
                showToast('success', 'Correo reenviado correctamente.');
            } else {
                showToast('warning', 'El correo sigue sin entregarse. Revisá la configuración de notificaciones.');
            }
        } catch (err) {
            showToast('error', err.message || 'No se pudo reenviar el correo.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Load the version list and hydrate the sidebar from the API as the single
    // source of truth: status + decision comment (for the banner) AND the
    // content of the current version. The artifact object passed in from the
    // chat always carries currentVersion's content, so we re-fetch here to land
    // on a consistent (version, content) pair instead of trusting the prop.
    useEffect(() => {
        if (!artifact.id) return;
        let cancelled = false;

        getArtifactVersions(artifact.id)
            .then((res) => {
                if (!cancelled) setVersions(res.versions || []);
            })
            .catch((err) => console.error('Failed to load versions:', err));

        getArtifact(artifact.id)
            .then((detail) => {
                if (cancelled) return;
                if (detail.status) setStatus(detail.status);
                if (detail.decisionComment !== undefined) setDecisionComment(detail.decisionComment);
                // Anchor the selector + content to the version the API returns
                // (currentVersion), keeping them consistent from the start.
                if (detail.version) setSelectedVersion(detail.version);
                if (detail.content !== undefined) setDisplayContent(detail.content);
            })
            .catch((err) => console.error('Failed to load artifact:', err));

        return () => {
            cancelled = true;
        };
    }, [artifact.id]);

    const handleVersionChange = async (version) => {
        if (!artifact.id || version === selectedVersion) return;
        setIsLoadingVersion(true);
        try {
            const detail = await getArtifact(artifact.id, version);
            setDisplayContent(detail.content);
            setSelectedVersion(version);
        } catch (err) {
            console.error('Failed to load version:', err);
        } finally {
            setIsLoadingVersion(false);
        }
    };

    const handleDownloadMd = () => {
        const blob = new Blob([displayContent], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = artifact.fileName;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleDownloadPdf = () => {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.top = '-9999px';
        iframe.style.left = '-9999px';
        iframe.style.width = '210mm';
        iframe.style.height = '297mm';
        document.body.appendChild(iframe);

        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open();
        doc.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>${artifact.title}</title>
                <style>
                    * { box-sizing: border-box; }
                    body {
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        font-size: 12px;
                        line-height: 1.7;
                        color: #1a1a1a;
                        padding: 20mm 15mm;
                        margin: 0;
                    }
                    h1 { font-size: 22px; margin: 24px 0 12px; }
                    h2 { font-size: 18px; margin: 20px 0 10px; }
                    h3 { font-size: 15px; margin: 16px 0 8px; }
                    h4, h5, h6 { font-size: 13px; margin: 12px 0 6px; }
                    p { margin: 8px 0; }
                    ul, ol { padding-left: 24px; margin: 8px 0; }
                    li { margin: 4px 0; }
                    table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 11px; }
                    th, td { border: 1px solid #d1d5db; padding: 6px 10px; text-align: left; }
                    th { background: #f3f4f6; font-weight: 600; }
                    code { font-size: 0.9em; padding: 1px 4px; background: #f3f4f6; border-radius: 3px; }
                    pre { background: #f3f4f6; padding: 12px; border-radius: 6px; overflow-x: auto; font-size: 11px; }
                    pre code { padding: 0; background: transparent; }
                    blockquote { margin: 12px 0; padding: 8px 16px; border-left: 3px solid #8b5cf6; background: #f5f3ff; }
                    hr { border: none; border-top: 1px solid #e5e7eb; margin: 16px 0; }
                    strong { font-weight: 600; }
                    @media print { body { padding: 0; } }
                </style>
            </head>
            <body>${contentRef.current.innerHTML}</body>
            </html>
        `);
        doc.close();

        setTimeout(() => {
            iframe.contentWindow.print();
            document.body.removeChild(iframe);
        }, 300);
    };

    return (
        <aside className="artifact-sidebar">
            <header className="artifact-sidebar__header">
                <h3 className="artifact-sidebar__title">{artifact.title}</h3>
                <button
                    className="artifact-sidebar__close"
                    onClick={onClose}
                    type="button"
                    aria-label="Close sidebar"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>

            <div className="artifact-sidebar__actions">
                {versions.length > 1 && (
                    <select
                        className="version-selector"
                        value={selectedVersion}
                        onChange={(e) => handleVersionChange(Number(e.target.value))}
                        disabled={isLoadingVersion}
                        aria-label="Select version"
                    >
                        {versions.map((v) => (
                            <option key={v.version} value={v.version}>
                                v{v.version} — {new Date(v.createdAt).toLocaleDateString()}
                            </option>
                        ))}
                    </select>
                )}

                <button className="btn-download" onClick={handleDownloadMd} type="button">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    .md
                </button>
                <button className="btn-download" onClick={handleDownloadPdf} type="button">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    .pdf
                </button>

                {canSubmit && (
                    <button
                        className="btn-submit-approval"
                        onClick={handleSubmitForApproval}
                        disabled={isSubmitting}
                        type="button"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13" />
                            <polygon points="22 2 15 22 11 13 2 9 22 2" />
                        </svg>
                        {isSubmitting
                            ? 'Enviando…'
                            : status === 'changes_requested'
                                ? 'Reenviar a revisión'
                                : 'Enviar a revisión'}
                    </button>
                )}

                {status === 'pending_approval' && !deliveryFailed && (
                    <span className="approval-status approval-status--pending">En revisión</span>
                )}

                {deliveryFailed && (
                    <button
                        className="btn-submit-approval btn-submit-approval--retry"
                        onClick={handleResend}
                        disabled={isSubmitting}
                        type="button"
                    >
                        {isSubmitting ? 'Reenviando…' : 'Reenviar correo'}
                    </button>
                )}
            </div>

            {selectedDecision && (
                <div className={`approval-feedback approval-feedback--${selectedDecision.variant}`} role="status">
                    <div className="approval-feedback__title">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        {selectedDecision.title}
                    </div>
                    {selectedDecision.comment && (
                        <p className="approval-feedback__comment">{selectedDecision.comment}</p>
                    )}
                    {selectedDecision.variant === 'changes' && !isBlocked && (
                        <p className="approval-feedback__hint">
                            Corregí el PRD según el comentario y volvé a enviarlo a revisión.
                        </p>
                    )}
                </div>
            )}

            {isLoadingVersion && (
                <div className="artifact-sidebar__loading">Loading version...</div>
            )}

            <div className="artifact-sidebar__content" ref={contentRef}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {displayContent}
                </ReactMarkdown>
            </div>

            <Toast
                open={toast.open}
                severity={toast.severity}
                message={toast.message}
                onFinished={closeToast}
                duration={6000}
            />
        </aside>
    );
}
